import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase';
import './terapiside.css';

// Funktion til at håndtere både markdown og HTML-indhold
const prepareContent = (content) => {
  if (!content) return '';

  // Hvis teksten allerede er HTML (fra rich text editor)
  if (content.includes('<p>') || content.includes('<h') || content.includes('<ul>')) {
    // Vi beholder HTML som den er, da formatering håndteres korrekt i rich text editor
    return content;
  }

  // Hvis det er markdown eller plain text
  // Konverter overskrifter
  let html = content.replace(/^## (.*?)$/gm, '<h3>$1</h3>');

  // Konverter fed tekst
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/\*(.*?)\*/g, '<strong>$1</strong>');

  // Konverter links - [text](url)
  html = html.replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');

  // Konverter bullet points
  html = html.replace(/^- (.*?)$/gm, '<li>$1</li>');
  
  // Del teksten på dobbelte linjeskift, hvilket normalt indikerer nye afsnit
  const paragraphs = html.split(/\n\s*\n/); // Split ved tomme linjer
  html = paragraphs.map(p => {
    const trimmedP = p.trim();
    if (trimmedP === '') return '';
    
    // Håndter lister
    if (trimmedP.includes('<li>')) {
      return `<ul>${trimmedP}</ul>`;
    }
    
    // Håndter overskrifter
    if (trimmedP.includes('<h3>')) {
      return trimmedP;
    }
    
    // Almindelige afsnit - bevar enkelte linjeskift som mellemrum
    // men vi erstatter ikke \n med <br> for at undgå uønskede linjeskift
    return `<p>${trimmedP.replace(/\n/g, ' ')}</p>`;
  }).join('\n\n'); // Tilføj afstand mellem afsnit

  return html;
};

// Helper funktion til at tjekke om en tekst er en gyldig URL
const isValidUrl = (text) => {
  try {
    if (text.match(/^(https?:\/\/|www\.)/i)) {
      const url = text.startsWith('www.') ? `https://${text}` : text;
      new URL(url);
      return url;
    }
    return false;
  } catch {
    return false;
  }
};

// Håndter link-indsættelse
const handleLinkInsertion = (formatType, editorRef) => {
  const selection = window.getSelection();
  if (!selection.rangeCount) return;
  
  const range = selection.getRangeAt(0);
  const selectedText = range.toString().trim();
  
  // Tjek om selektionen er inden for editoren
  if (!editorRef.current.contains(range.commonAncestorContainer)) {
    return;
  }
  
  // Tjek om den valgte tekst er en URL
  const url = isValidUrl(selectedText) ? selectedText : prompt('Indtast URL:', 'https://');
  
  if (url) {
    const linkText = selectedText || prompt('Indtast link tekst:', '');
    
    if (linkText) {
      // Indsæt link direkte i HTML
      const linkElement = document.createElement('a');
      linkElement.href = url;
      linkElement.target = '_blank';
      linkElement.rel = 'noopener noreferrer';
      linkElement.textContent = linkText;
      
      // Indsæt linket
      range.deleteContents();
      range.insertNode(linkElement);
      
      // Flyt cursoren efter linket
      range.setStartAfter(linkElement);
      range.setEndAfter(linkElement);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  }
};

const TerapiSide = () => {
  const { id } = useParams();
  const [terapiData, setTerapiData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchTerapiData = async () => {
      try {
        // Hent alle terapiområder
        const terapiOmraderDocRef = doc(db, 'content', 'terapiOmrader');
        const terapiOmraderDoc = await getDoc(terapiOmraderDocRef);
        
        if (terapiOmraderDoc.exists() && terapiOmraderDoc.data().omrader) {
          const omrader = terapiOmraderDoc.data().omrader;
          const område = omrader.find(o => o.id === id);
          
          if (område) {
            setTerapiData(område);
          }
        }
      } catch (error) {
        console.error('Fejl ved hentning af terapidata:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      fetchTerapiData();
    }
  }, [id]);

  if (isLoading) {
    return <div className="terapiside-loading">Indlæser...</div>;
  }

  if (!terapiData) {
    return (
      <div className="terapiside-not-found">
        <h1>Terapiområde ikke fundet</h1>
        <p>Vi kunne ikke finde det terapiområde, du leder efter.</p>
        <a href="/terapi">Tilbage til Terapi</a>
      </div>
    );
  }

  return (
    <div className="terapiside">
      <div className="terapiside-container">
        <h1 className="terapiside-title">{terapiData.title}</h1>
        
        {terapiData.imageUrl && (
          <div className="terapiside-image">
            <img src={terapiData.imageUrl} alt={terapiData.title} />
          </div>
        )}
        
        <div className="terapiside-content">
          {terapiData.fullDescription ? (
            <div className="full-description" dangerouslySetInnerHTML={{ __html: prepareContent(terapiData.fullDescription) }} />
          ) : (
            <p>{terapiData.description}</p>
          )}
        </div>
        
        <div className="terapiside-back">
          <a href="/terapi">Tilbage til oversigten</a>
        </div>
      </div>
    </div>
  );
};

export default TerapiSide;
